import { Checkbox, Collapse, RotatingChevron } from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";

import { ConceptNode } from "../../Concepts.interface";

import {
  fieldsetCss,
  innerTagStyle,
  recursiveTagCheckboxCss,
  recursiveTagLabelCss,
  collapseButton,
  collapseLabel,
  collapseStyle,
  collapseTitleContainer,
} from "./RecursiveConceptTree.styles";

const CheckboxWithTrackEvent = withTrackEvent(Checkbox);

interface RecursiveConceptTreeProps {
  node: ConceptNode;
  selectedIds: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
  level?: number;
  isNodeSelected: (id: string) => boolean;
}

export const RecursiveConceptTree = ({
  node,
  selectedIds,
  onToggle,
  level = 0,
  isNodeSelected,
}: RecursiveConceptTreeProps) => {
  // Skip rendering the root Document node which is usually the subject nodes i.e. "ELA"
  if (node.parentId === null && node.isFolder && level === 0) {
    return (
      <>
        {node.children.map((child: ConceptNode) => {
          return (
            <Collapse
              key={child.id}
              initialIsHidden={false}
              renderButtonContents={(isHidden, timeout) => (
                <div css={collapseTitleContainer}>
                  <CheckboxWithTrackEvent
                    checked={isNodeSelected(child.id)}
                    onChange={(e) => onToggle(child.id, e.target.checked)}
                    label={child.name}
                    __cssFor={{
                      labelText: collapseLabel,
                    }}
                    objectType={objectTypes.user}
                    actionName={actionNames.select}
                    streamProperties={{
                      description: `Toggle concept for search filter: ${child.name}`,
                    }}
                    dataProps={{
                      "data-qa-selector": `concept-group-${child.id}`,
                      "data-testid": `concept-group-${child.id}`,
                    }}
                  />
                  <RotatingChevron
                    isDown={isHidden}
                    timeout={timeout || 0}
                    __cssFor={{ root: collapseButton }}
                  />
                </div>
              )}
              __cssFor={collapseStyle}
            >
              {child.children.length > 0 ? (
                <>
                  {child.children.map((grandChild: ConceptNode) => (
                    <RecursiveConceptTree
                      key={grandChild.id}
                      node={grandChild}
                      selectedIds={selectedIds}
                      onToggle={onToggle}
                      level={level + 2}
                      isNodeSelected={isNodeSelected}
                    />
                  ))}
                </>
              ) : null}
            </Collapse>
          );
        })}
      </>
    );
  }

  return (
    <fieldset style={fieldsetCss(level)}>
      <CheckboxWithTrackEvent
        key={node.id}
        checked={isNodeSelected(node.id)}
        onChange={(e) => onToggle(node.id, e.target.checked)}
        label={node.name}
        __cssFor={{
          root: recursiveTagCheckboxCss,
          labelText: recursiveTagLabelCss(level),
        }}
        objectType={objectTypes.user}
        actionName={actionNames.select}
        streamProperties={{
          description: `Toggle concept for search filter: ${node.name}`,
        }}
        dataProps={{
          "data-qa-selector": `concept-child-${node.id}`,
          "data-testid": `concept-child-${node.id}`,
        }}
      />
      {node.children.length > 0 ? (
        <div css={innerTagStyle}>
          {node.children.map((child: ConceptNode) => (
            <RecursiveConceptTree
              key={child.id}
              node={child}
              selectedIds={selectedIds}
              onToggle={onToggle}
              level={level + 1}
              isNodeSelected={isNodeSelected}
            />
          ))}
        </div>
      ) : null}
    </fieldset>
  );
};
